"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useI18n } from "@/i18n/I18nProvider";
import { localizeError } from "@/i18n/errors";
import { skipsStaffSession } from "@/lib/public-paths";
import { useIsSiteHost } from "@/components/SiteHostContext";
import { disablePush } from "@/lib/browser-notifications";
import type { Locale } from "@/i18n/config";
import type { Property, SafeUser, Role, ScopeMode, UserTenantInfo, AuthMeResponse } from "@/lib/types";

interface AppContextType {
  user: SafeUser | null;
  tenantId: string;
  role: Role | null;
  scopeMode: ScopeMode | null;
  allowedPropertyIds: string[];
  availableTenants: UserTenantInfo[];
  properties: Property[];
  selectedPropertyId: string;
  setSelectedPropertyId: (id: string) => void;
  unreadCount: number;
  billing: { locked: boolean; features: string[] } | null;
  /** Set while a platform admin is viewing as this user. */
  impersonation: { by_email: string; expires_at: string } | null;
  exitImpersonation: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  refreshProperties: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchApi: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  fetchWithTenant: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

async function localizeResponse(res: Response, locale: Locale): Promise<Response> {
  const type = res.headers.get("content-type") || "";
  if (!type.includes("json") || res.status === 204) return res;
  try {
    const text = await res.clone().text();
    const body = JSON.parse(text);
    if (!body || typeof body !== "object" || typeof body.error !== "string") return res;
    return new Response(JSON.stringify({ ...body, error: localizeError(locale, body.error) }), {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  } catch {
    return res;
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { locale } = useI18n();
  const isSiteHost = useIsSiteHost();
  const [user, setUser] = useState<SafeUser | null>(null);
  const [tenantId, setTenantId] = useState<string>("");
  const [role, setRole] = useState<Role | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode | null>(null);
  const [allowedPropertyIds, setAllowedPropertyIds] = useState<string[]>([]);
  const [availableTenants, setAvailableTenants] = useState<UserTenantInfo[]>([]);

  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("all");
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [billing, setBilling] = useState<{ locked: boolean; features: string[] } | null>(null);
  const [impersonation, setImpersonation] = useState<{ by_email: string; expires_at: string } | null>(null);
  const refreshAuthRef = useRef<() => Promise<void>>(async () => {});

  const fetchApi = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const res = await fetch(input, {
        ...init,
        credentials: "same-origin",
      });

      // Subscription lapsed mid-session: re-read the auth profile so the app switches to the paywall.
      if (res.status === 402) {
        try {
          const body = await res.clone().json();
          if (typeof body.error === "string" && body.error.startsWith("SUBSCRIPTION_REQUIRED")) void refreshAuthRef.current();
        } catch {
          // non-JSON body
        }
      }

      if (res.status === 401) {
        if (typeof window !== "undefined" && !isSiteHost && !skipsStaffSession(window.location.pathname)) {
          router.push("/login");
        }
      }

      return localizeResponse(res, locale);
    },
    [router, locale, isSiteHost]
  );

  const refreshAuth = useCallback(async () => {
    try {
      const res = await fetchApi("/api/auth/me");
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          const data = json.data as AuthMeResponse;
          setUser(data.user);
          setTenantId(data.activeTenant.id);
          setRole(data.activeTenant.role);
          setScopeMode(data.activeTenant.scopeMode);
          setAllowedPropertyIds(data.activeTenant.allowedPropertyIds || []);
          setAvailableTenants(data.availableTenants || []);
          setImpersonation(data.impersonation ?? null);
          setBilling(data.billing ? { locked: data.billing.locked, features: data.billing.features } : null);
        }
      }
    } catch (err) {
      console.error("Failed to load auth context:", err);
    }
  }, [fetchApi]);

  useEffect(() => {
    refreshAuthRef.current = refreshAuth;
  }, [refreshAuth]);

  const refreshProperties = useCallback(async () => {
    try {
      const res = await fetchApi("/api/properties");
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setProperties(json.data);
        }
      }
    } catch (err) {
      console.error("Failed to load properties:", err);
    }
  }, [fetchApi]);

  const refreshNotifications = useCallback(async () => {
    try {
      const res = await fetchApi("/api/notifications?unread=true");
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setUnreadCount(json.unread_count || 0);
        }
      }
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  }, [fetchApi]);

  const switchTenant = async (newTenantId: string): Promise<boolean> => {
    try {
      const res = await fetchApi("/api/auth/switch-tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: newTenantId }),
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          const data = json.data as AuthMeResponse;
          setUser(data.user);
          setTenantId(data.activeTenant.id);
          setRole(data.activeTenant.role);
          setScopeMode(data.activeTenant.scopeMode);
          setAllowedPropertyIds(data.activeTenant.allowedPropertyIds || []);
          setAvailableTenants(data.availableTenants || []);
          setBilling(data.billing ? { locked: data.billing.locked, features: data.billing.features } : null);
          setSelectedPropertyId("all");
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error("Failed to switch tenant:", err);
      return false;
    }
  };

  const exitImpersonation = async (): Promise<void> => {
    try {
      const res = await fetchApi("/api/auth/impersonate/exit", { method: "POST" });
      const json = await res.json();
      // Full reload so no customer data lingers in client state.
      window.location.href = json.success && json.data?.restored ? "/admin" : "/login";
    } catch (err) {
      console.error("Failed to exit impersonation:", err);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // A signed-out device must stop receiving pushes about this organization. The preference stays, so signing in again
      // re-registers it.
      await disablePush();
      await fetch("/api/auth/logout", { method: "POST" });
      setUser(null);
      setTenantId("");
      setRole(null);
      router.push("/login");
    } catch (err) {
      console.error("Failed to logout:", err);
    }
  };

  // Load auth context on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (!isSiteHost && !skipsStaffSession(path)) {
        refreshAuth();
      }
    }
  }, [refreshAuth, isSiteHost]);

  // Refresh properties and notifications whenever tenant changes
  useEffect(() => {
    if (tenantId) {
      refreshProperties();
      refreshNotifications();
    }
  }, [tenantId, refreshProperties, refreshNotifications]);

  return (
    <AppContext.Provider
      value={{
        user,
        tenantId,
        role,
        scopeMode,
        allowedPropertyIds,
        availableTenants,
        properties,
        selectedPropertyId,
        setSelectedPropertyId,
        unreadCount,
        billing,
        impersonation,
        exitImpersonation,
        refreshAuth,
        refreshNotifications,
        refreshProperties,
        switchTenant,
        logout,
        fetchApi,
        fetchWithTenant: fetchApi,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
